/* istanbul ignore file */
import {
  setOpenCategories,
} from "@/core/redux/slices/specification";
import { useDispatch, useSelector } from "@/core/redux/store";
import {
  ISpecificationCategoryResponse,
  ISpecificationItemResponse,
} from "@/shared/services/interface/responses/ISpecificationResponse";
import { ChevronRight, ChevronDown } from "lucide-react";
import { useEffect, useMemo } from "react";
import { SpecificationCategoryList } from "../category-list";
import { CategoryItem } from "../category-item/category-item";
import { cn } from "@/lib/utils";
import { findAllParentCategoryIds } from "@/features/dashboard/pages/specification/pages/create/category-sidebar/category-sidebar-content/utils/category-helpers";
import { useSidebarItemFilter } from "../../use-sidebar-item-filter";


export default function RenderCollapsible({
  item,
  priority,
  parentId = null,
}: {
  item: ISpecificationCategoryResponse;
  priority: string;
  parentId?: string | null;
}) {
  const dispatch = useDispatch();
  const specification = useSelector((s) => s.specification);
  const sidebarWidth = useSelector((s) => s.specificationList.sidebarWidth);
  const { shouldShowItem, hasVisibleChildren, hasActiveFilter } = useSidebarItemFilter();

  const activeStep = specification.activeSpecificationStep;
  const activeCategory = activeStep.category || { id: "" };
  const filteredCategories = specification.filteredCategories;

  // Only highlight when a real item is selected (activeStep.id is not empty)
  const hasActiveItem = !!activeStep.id;

  // Find all active category IDs (selected item's category + all parents)
  const activeCategoryIds = useMemo(() => {
    if (
      !hasActiveItem ||
      !activeCategory?.id ||
      !filteredCategories ||
      filteredCategories.length === 0
    ) {
      return [];
    }
    return findAllParentCategoryIds(filteredCategories, activeCategory.id);
  }, [hasActiveItem, activeCategory?.id, filteredCategories]);

  // Check if this category is active (either directly selected or a parent of selected item)
  const isCategoryActive = useMemo(() => {
    return activeCategoryIds.includes(item.id);
  }, [activeCategoryIds, item.id]);

  // Get isOpen state directly from Redux to stay in sync
  const isOpen = specification.openCategories.includes(item.id);

  useEffect(() => {
    if (specification.specification.specification.status === 1 && !isOpen) {
      dispatch(setOpenCategories(item.id));
    }
  }, [
    specification.specification.specification.status,
    isOpen,
    item.id,
    dispatch,
    specification.openCategories,
  ]);

  // Auto-expand category when it contains the active item
  const activeItemId = specification.activeSpecificationStep.id;
  useEffect(() => {
    if (isCategoryActive && !isOpen && activeItemId) {
      dispatch(setOpenCategories(item.id));
    }
  }, [isCategoryActive, activeItemId, isOpen]);

  // TOGGLE - only expand/collapse, don't change active content
  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();

    // setOpenCategories already toggles the category in Redux state
    dispatch(setOpenCategories(item.id));
  };

  const hasChildren =
    item.children && Array.isArray(item.children) && item.children.length > 0;

  // When filter is active, hide categories that have no visible children
  if (hasActiveFilter && !hasVisibleChildren(item.children)) {
    return null;
  }

  return (
    <li className="list-none relative">
      <div
        className={cn(
          "relative flex text-xs items-center w-full h-[28px] py-1.5 px-1 cursor-pointer rounded-[6px] justify-between hover:bg-[#F4F4F5] transition-colors duration-100",
          isOpen && isCategoryActive ? "text-[#18181B] font-semibold" : "text-[#3F3F46]"
        )}
        onClick={handleToggle}
      >
        {/* LEFT SECTION: TOGGLE + PRIORITY + NAME */}
        <div className="flex items-center gap-0 flex-1 min-w-0">
          <button className="h-4 w-4 flex items-center justify-center flex-shrink-0">
            {isOpen ? (
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
            )}
          </button>

          {/* PRIORITY */}
          <span
            className="text-xs font-500 mr-1 flex-shrink-0"
          >
            {priority}.
          </span>

          {/* NAME */}
          <div className="flex-1 truncate text-xs">
            {item.name}
          </div>
        </div>
      </div>

      {/* CHILDREN */}
      {isOpen && hasChildren && item.children && (() => {
        const subCategories = item.children.filter(
          (c) => c.type === "category"
        ) as ISpecificationCategoryResponse[];
        const subItems = item.children.filter((c) => c.type === "item");

        // Apply sidebar filter to items
        const visibleSubItems = hasActiveFilter
          ? subItems.filter((child) => shouldShowItem(child.id))
          : subItems;

        return (
          <div
            className={cn(
              "flex flex-col gap-0",
              // Show the guide line only for root categories (e.g. 1.), not nested ones (e.g. 1.1).
              parentId == null ? "border-l border-[#E5E7EB] ml-[12px]" : "ml-0"
            )}
          >
            {subCategories.length > 0 && (
              <SpecificationCategoryList
                categories={subCategories}
                parentPriority={priority}
                parentId={item.id}
              />
            )}
            {visibleSubItems.map((child, idx) => (
              <CategoryItem
                key={child.id}
                subItem={child}
                sidebarWidth={sidebarWidth}
                priority={priority}
                subIndex={idx}
                item={item}
              />
            ))}
          </div>
        );
      })()}
    </li>
  );
}
